import type { components } from '../../api/modelos'

export type ProductoCatalogo = components['schemas']['ProductoCatalogo']

/**
 * El catalogo de Odoo, leido para poder ofrecerlo en un desplegable.
 *
 * El modo individual **no deja escribir el bono a mano**: elige un producto que
 * existe. El backend vuelve a buscarlo por nombre al componer el pedido, y esa
 * busqueda falla si el nombre no casa con exactamente un producto del catalogo;
 * ofrecer solo lo que hay hace imposible ese fallo. Es lo contrario del modo
 * masivo, donde el texto lo escribe una persona y el error es inevitable, y por
 * eso alli se marca la linea en vez de impedirlo.
 */

export interface BonoCatalogo extends ProductoCatalogo {
  /** Garrafas del bono. Es la `N` de `1 x BONO N`, `N x ENVASE`, `-N x DEVOLUCION`. */
  unidades: number
  litraje: string
  /** Lo que sobra del nombre: `SIN FUENTE`, `FONTANA`... Vacio si no hay. */
  variante: string
}

/** `18.9L`, `20L`, `12.8L`. El punto es parte del nombre: `189L` es otro producto. */
const LITRAJE = /^(\d+(?:\.\d+)?)L$/
const ENTERO = /^\d+$/

/**
 * El nombre partido en palabras comparables. Mismo criterio que el backend:
 * mayusculas, sin tildes y la `L` suelta pegada al numero que la precede, para
 * que `18,9 L` y `18.9L` sean la misma palabra.
 */
function piezas(nombre: string): string[] {
  const texto = nombre
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toUpperCase()
    .replace(/,/g, '.')
  const salida: string[] = []
  for (const trozo of texto.split(/\s+/)) {
    const limpio = trozo.replace(/[^A-Z0-9.]/g, '').replace(/^\.+|\.+$/g, '')
    if (limpio === '') continue
    const ultimo = salida[salida.length - 1]
    if (limpio === 'L' && ultimo !== undefined && /^\d/.test(ultimo)) {
      salida[salida.length - 1] = `${ultimo}L`
    } else {
      salida.push(limpio)
    }
  }
  return salida
}

/**
 * Si ese producto es un bono.
 *
 * Publica porque el selector de productos necesita **marcarlos**: un bono
 * anadido como linea suelta entra solo, sin sus envases ni su devolucion, y eso
 * hay que decirlo donde se pulsa.
 */
export function esBono(nombre: string): boolean {
  return piezas(nombre)[0] === 'BONO'
}

/**
 * Si ese producto es un pack (agua embotellada 1,5L / 0,5L), la opcion de
 * "¿necesitas algo mas?" del cuestionario. Se reconocen por la palabra PACK.
 */
export function esPack(nombre: string): boolean {
  return piezas(nombre).includes('PACK')
}

/** Desde cuantos litros un envase es un botellon de reparto y no una botella. */
const LITROS_BOTELLON = 5

/**
 * Si ese producto es un botellon suelto (un envase de 5 L o mas, de la marca que
 * sea), sin ser bono ni pack ni una linea de envase/devolucion. Son los
 * botellones por unidades que la spec (R9) manda ofrecer a los clientes comunes.
 */
export function esBotellonSuelto(nombre: string): boolean {
  const p = piezas(nombre);
  if (esBono(nombre) || esPack(nombre)) return false;
  // Envases y devoluciones no son producto vendible: son las lineas de fianza.
  if (p.includes('ENVASE') || p.includes('DEVOLUCION')) return false;
  return p.some((pieza) => {
    const litros = LITRAJE.exec(pieza);
    return litros !== null && Number(litros[1]) >= LITROS_BOTELLON;
  });
}

/** La categoria de un producto del catalogo, para agruparlo en el panel. */
export type CategoriaProducto = 'bono' | 'pack' | 'suelto' | 'otro';

export function categoriaDe(nombre: string): CategoriaProducto {
  if (esBono(nombre)) return 'bono';
  if (esPack(nombre)) return 'pack';
  if (esBotellonSuelto(nombre)) return 'suelto';
  return 'otro';
}

/** Los packs del catalogo, ordenados por nombre. */
export function packsDelCatalogo(productos: ProductoCatalogo[]): ProductoCatalogo[] {
  return productos
    .filter((p) => esPack(p.nombre))
    .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
}

/** Los botellones sueltos del catalogo, ordenados por nombre. */
export function sueltosDelCatalogo(productos: ProductoCatalogo[]): ProductoCatalogo[] {
  return productos
    .filter((p) => esBotellonSuelto(p.nombre))
    .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
}

function leerBono(producto: ProductoCatalogo): BonoCatalogo | null {
  const trozos = piezas(producto.nombre)
  if (trozos[0] !== 'BONO') return null

  const resto = trozos.slice(1)
  // Ni las unidades ni el litraje estan en una posicion fija: el catalogo
  // escribe `BONO 4 12.8L SIN FUENTE` y `BONO 4 SIN FUENTE 18.9L`.
  const indiceUnidades = resto.findIndex((pieza) => ENTERO.test(pieza))
  const indiceLitraje = resto.findIndex((pieza) => LITRAJE.test(pieza))
  if (indiceUnidades < 0 || indiceLitraje < 0) return null

  const litraje = LITRAJE.exec(resto[indiceLitraje])?.[1] ?? ''
  const variante = resto
    .filter((_, indice) => indice !== indiceUnidades && indice !== indiceLitraje)
    .join(' ')

  return {
    ...producto,
    unidades: Number(resto[indiceUnidades]),
    litraje,
    variante,
  }
}

/**
 * Los bonos ofrecibles, ordenados por litraje y luego por garrafas. El id de
 * Odoo no ordena nada util: es el orden en que se dieron de alta.
 */
export function bonosDelCatalogo(productos: ProductoCatalogo[]): BonoCatalogo[] {
  const bonos = productos
    .map(leerBono)
    .filter((bono): bono is BonoCatalogo => bono !== null)

  return bonos.sort(
    (a, b) =>
      Number(a.litraje) - Number(b.litraje) ||
      a.unidades - b.unidades ||
      a.nombre.localeCompare(b.nombre, 'es'),
  )
}

/**
 * Todo lo que se puede anadir como linea suelta: **el catalogo entero**.
 *
 * Los bonos estuvieron fuera un tiempo, con el argumento de que anadidos asi
 * entran como una sola linea -sin envases ni devolucion- y el pedido saldria
 * mal sin que nada lo avisara. El argumento era bueno pero la conclusion no:
 * dejaba sin salida el caso en que la composicion automatica **no puede**
 * montar el bono. Un `BONO 4 20L` no se puede componer hoy porque `ENVASE 20L`
 * casa con dos productos del catalogo, y el propio error dice "elige el
 * producto a mano" mientras la pantalla no dejaba elegir nada.
 *
 * Asi que entran todos, y el riesgo se cubre donde toca: el selector **marca**
 * los bonos y avisa de que van solos. Un aviso visible es mejor que una
 * ausencia inexplicable.
 */
export function productosSueltos(productos: ProductoCatalogo[]): ProductoCatalogo[] {
  return productos
}

/**
 * Los productos que casan con lo tecleado, en el orden del catalogo.
 *
 * Se busca por palabras sueltas y en cualquier orden: con 112 productos,
 * "manantial 18" tiene que encontrar `MANANTIAL 18.9L` sin obligar a escribir el
 * nombre exacto. Se comparan las mismas piezas normalizadas que usa el resto
 * del modulo, asi que las tildes y el punto del litraje no estorban.
 */
export function filtrarProductos(
  productos: ProductoCatalogo[],
  consulta: string,
): ProductoCatalogo[] {
  const buscadas = piezas(consulta)
  if (buscadas.length === 0) return productos
  return productos.filter((producto) => {
    const texto = piezas(producto.nombre).join(' ')
    return buscadas.every((palabra) => texto.includes(palabra))
  })
}
